import React, { useState } from 'react';
import { Table, Switch, Alert, Space, Button, Empty, message, Card, Row, Col } from 'antd';
import type { TableColumnsType } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import ScanSettingLogDrawer from './ScanSettingLogDrawer';
import {
    LIST_ACCOUNT_SCAN_SETTINGS,
    UPDATE_ACCOUNT_SCAN_SETTING,
    BATCH_UPDATE_WAREHOUSE_SCAN_SETTING,
    ListAccountScanSettingsResponse,
    UpdateAccountScanSettingResponse,
    BatchUpdateWarehouseScanSettingResponse,
    UpdateScanSettingInput,
    AccountScanSetting,
    ScanSettingField,
} from '../graphql/pdaScanConfigQueries';

interface Props {
    warehouseId?: number;
}

const AccountScanSettingTable: React.FC<Props> = ({ warehouseId }) => {
    const { t } = useTranslation();
    const [logAccountId, setLogAccountId] = useState<string | undefined>();
    const [logOpen, setLogOpen] = useState(false);

    const { data, loading } = useQuery<ListAccountScanSettingsResponse>(LIST_ACCOUNT_SCAN_SETTINGS, {
        variables: { warehouseId },
        skip: !warehouseId,
        fetchPolicy: 'cache-and-network',
    });
    const [update] = useMutation<UpdateAccountScanSettingResponse>(UPDATE_ACCOUNT_SCAN_SETTING);
    const [batchUpdate] = useMutation<BatchUpdateWarehouseScanSettingResponse>(BATCH_UPDATE_WAREHOUSE_SCAN_SETTING);

    const FIELD_TO_KEY: Record<ScanSettingField, 'masterEnabled' | 'vibrateEnabled' | 'breathingLightEnabled' | 'soundEnabled'> = {
        MASTER: 'masterEnabled',
        VIBRATE: 'vibrateEnabled',
        BREATHING_LIGHT: 'breathingLightEnabled',
        SOUND: 'soundEnabled',
    };

    const handleUpdate = async (accountId: string, input: UpdateScanSettingInput) => {
        if (!warehouseId) return;
        try {
            await update({
                variables: { accountId: Number(accountId), warehouseId, input },
                update: (cache, { data: mut }) => {
                    if (!mut?.updateAccountScanSetting) return;
                    const cached = cache.readQuery<ListAccountScanSettingsResponse>({
                        query: LIST_ACCOUNT_SCAN_SETTINGS,
                        variables: { warehouseId },
                    });
                    if (!cached) return;
                    cache.writeQuery({
                        query: LIST_ACCOUNT_SCAN_SETTINGS,
                        variables: { warehouseId },
                        data: {
                            listAccountScanSettings: cached.listAccountScanSettings.map(a =>
                                a.accountId === accountId ? { ...a, setting: mut.updateAccountScanSetting } : a,
                            ),
                        },
                    });
                },
            });
            message.success(t('pdaScanConfig.updateSuccess'));
        } catch (e) {
            message.error(t('pdaScanConfig.updateFailed'));
        }
    };

    const handleBatch = async (field: ScanSettingField, enabled: boolean) => {
        if (!warehouseId) return;
        try {
            const res = await batchUpdate({
                variables: { warehouseId, field, enabled },
                update: cache => {
                    const cached = cache.readQuery<ListAccountScanSettingsResponse>({
                        query: LIST_ACCOUNT_SCAN_SETTINGS,
                        variables: { warehouseId },
                    });
                    if (!cached) return;
                    const key = FIELD_TO_KEY[field];
                    cache.writeQuery({
                        query: LIST_ACCOUNT_SCAN_SETTINGS,
                        variables: { warehouseId },
                        data: {
                            listAccountScanSettings: cached.listAccountScanSettings.map(a => ({
                                ...a,
                                setting: { ...a.setting, [key]: enabled },
                            })),
                        },
                    });
                },
            });
            const affected = res.data?.batchUpdateWarehouseScanSetting ?? 0;
            message.success(t('pdaScanConfig.batchSuccess', { count: affected }));
        } catch (e) {
            message.error(t('pdaScanConfig.batchFailed'));
        }
    };

    const openLog = (accountId: string) => {
        setLogAccountId(accountId);
        setLogOpen(true);
    };

    const columns: TableColumnsType<AccountScanSetting> = [
        {
            title: t('pdaScanConfig.account.accountId'),
            dataIndex: 'accountId',
            key: 'accountId',
            width: 100,
        },
        {
            title: t('pdaScanConfig.account.accountCode'),
            key: 'accountCode',
            width: 160,
            render: (_, r) => r.accountLoginName ?? '-',
        },
        {
            title: t('pdaScanConfig.account.accountName'),
            dataIndex: 'accountName',
            key: 'accountName',
        },
        {
            title: t('pdaScanConfig.account.master'),
            key: 'master',
            render: (_, r) => (
                <Switch checked={r.setting.masterEnabled} onChange={(v) => handleUpdate(r.accountId, { masterEnabled: v })} />
            ),
        },
        {
            title: t('pdaScanConfig.account.vibrate'),
            key: 'vibrate',
            render: (_, r) => (
                <Switch checked={r.setting.vibrateEnabled} disabled={!r.setting.masterEnabled} onChange={(v) => handleUpdate(r.accountId, { vibrateEnabled: v })} />
            ),
        },
        // todo <sunruixin>: 呼吸灯列暂时隐藏，硬件接通后恢复（数据/接口/FIELD_TO_KEY 全保留）
        {
            title: t('pdaScanConfig.account.sound'),
            key: 'sound',
            render: (_, r) => (
                <Switch checked={r.setting.soundEnabled} disabled={!r.setting.masterEnabled} onChange={(v) => handleUpdate(r.accountId, { soundEnabled: v })} />
            ),
        },
        {
            title: t('pdaScanConfig.account.soundId'),
            key: 'soundId',
            render: (_, r) => {
                if (!r.setting.masterEnabled || !r.setting.soundEnabled) return <span style={{ color: '#bbb' }}>-</span>;
                return t(`pdaScanConfig.soundName.${r.setting.soundId}`, { defaultValue: r.setting.soundId });
            },
        },
        {
            title: t('pdaScanConfig.account.action'),
            key: 'action',
            render: (_, r) => (
                <Button type="link" size="small" onClick={() => openLog(r.accountId)}>
                    {t('pdaScanConfig.account.viewLog')}
                </Button>
            ),
        },
    ];

    if (!warehouseId) {
        return <Empty description={t('pdaScanConfig.selectWarehouseFromTree')} />;
    }

    const batchCard = (field: ScanSettingField, label: string) => (
        <Card
            size="small"
            style={{
                borderRadius: 8,
                minWidth: 200,
                flex: 1,
            }}
        >
            <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>{label}</div>
            <Space>
                <Button
                    type="primary"
                    size="small"
                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                    onClick={() => handleBatch(field, true)}
                >
                    {t('pdaScanConfig.batchEnableAll')}
                </Button>
                <Button
                    danger
                    size="small"
                    onClick={() => handleBatch(field, false)}
                >
                    {t('pdaScanConfig.batchDisableAll')}
                </Button>
            </Space>
        </Card>
    );

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Alert type="info" showIcon message={t('pdaScanConfig.effectTip')} />
            <Row gutter={12} style={{ marginBottom: 4 }}>
                <Col flex="1">{batchCard('MASTER', t('pdaScanConfig.account.master'))}</Col>
                <Col flex="1">{batchCard('VIBRATE', t('pdaScanConfig.account.vibrate'))}</Col>
                {/* todo <sunruixin>: 呼吸灯一键开关暂时隐藏，硬件接通后恢复 */}
                <Col flex="1">{batchCard('SOUND', t('pdaScanConfig.account.sound'))}</Col>
            </Row>
            <Table<AccountScanSetting>
                rowKey="accountId"
                loading={loading}
                columns={columns}
                dataSource={data?.listAccountScanSettings ?? []}
                pagination={false}
            />
            <ScanSettingLogDrawer
                open={logOpen}
                onClose={() => setLogOpen(false)}
                accountId={logAccountId}
            />
        </Space>
    );
};

export default AccountScanSettingTable;
